import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, UserCircle, Bookmark, ChevronRight, X, Check } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const KEY = "cavilo_onboarded_v1";

const STEPS = [
  {
    icon: UserCircle,
    accent: "#9D00FF",
    accent2: "#C580FF",
    tag: "Step 1 · Career Profile",
    title: "Tell us who you are.",
    body: "Your title, skills, target roles and location. The stronger your profile, the sharper every match.",
    cta: "Set up my profile",
    to: "/career-profile",
    tips: ["Auto-filled from your CV", "60 seconds to complete", "Update anytime"],
  },
  {
    icon: Sparkles,
    accent: "#00E5FF",
    accent2: "#80FFEA",
    tag: "Step 2 · Job Agent",
    title: "Meet your Job Agent.",
    body: "Verified roles ranked by fit. Every card shows why it matches, what's missing, and a one-tap kit with cover letter, LinkedIn note, HR email and interview prep.",
    cta: "See my matches",
    to: "/job-agent",
    tips: ["Ranked by match score", "One-tap Application Pack", "Real jobs only, no samples"],
  },
  {
    icon: Bookmark,
    accent: "#FF007F",
    accent2: "#FF6DAE",
    tag: "Step 3 · Pipeline",
    title: "Track every application.",
    body: "Save roles to a 7-stage board. Add notes, applied dates, and follow-up reminders so nothing slips.",
    cta: "Start tracking",
    to: "/pipeline",
    tips: ["Matched → Offer stages", "Notes & follow-up dates", "Weekly digest by email"],
  },
];

export default function Onboarding({ forceOpen = false, onClose }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!user) { setOpen(false); return; }
    if (forceOpen) { setOpen(true); setI(0); return; }
    const seen = localStorage.getItem(KEY);
    if (!seen) setOpen(true);
  }, [user, forceOpen]);

  const dismiss = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
    onClose && onClose();
  };

  const next = () => {
    if (i < STEPS.length - 1) setI(i + 1);
    else dismiss();
  };

  const goTo = () => {
    const to = STEPS[i].to;
    dismiss();
    nav(to);
  };

  if (!open) return null;
  const s = STEPS[i];
  const Icon = s.icon;

  return (
    <div
      data-testid="onboarding-modal"
      className="fixed inset-0 z-[100] grid place-items-center p-4 sm:p-6 fade-up"
      style={{ background: "rgba(3,3,8,0.85)", backdropFilter: "blur(12px)" }}
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl rounded-[2rem] overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0A0A12 0%, #14142B 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: `0 40px 80px -20px rgba(0,0,0,0.6), 0 0 40px ${s.accent}44`,
        }}
      >
        {/* Ambient glow */}
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full blur-3xl opacity-40 pointer-events-none" style={{ background: s.accent }} />
        <div className="absolute -left-20 -bottom-24 w-72 h-72 rounded-full blur-3xl opacity-30 pointer-events-none" style={{ background: s.accent2 }} />

        {/* Close */}
        <button
          data-testid="onboarding-close"
          onClick={dismiss}
          className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 z-10"
          aria-label="Skip tour"
        >
          <X size={16} />
        </button>

        <div className="relative p-8 sm:p-10">
          {/* Icon medallion */}
          <div
            className="w-16 h-16 rounded-2xl grid place-items-center mb-5"
            style={{
              background: `linear-gradient(135deg, ${s.accent}33, ${s.accent2}22)`,
              border: `1px solid ${s.accent}55`,
              boxShadow: `0 0 32px ${s.accent}66`,
            }}
          >
            <Icon size={28} style={{ color: s.accent2 }} />
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: s.accent2 }}>
            <Sparkles size={11} /> {s.tag}
          </div>

          <h2 data-testid={`onboarding-step-${i + 1}-title`} className="font-display font-black text-3xl sm:text-4xl mt-4 tracking-tighter leading-tight text-white">
            {s.title}
          </h2>
          <p className="mt-3 text-slate-300 text-base leading-relaxed max-w-lg">{s.body}</p>

          {/* Tips */}
          <ul className="mt-5 space-y-2">
            {s.tips.map((t) => (
              <li key={t} className="text-sm text-slate-300 flex items-center gap-2">
                <span className="grid place-items-center w-4 h-4 rounded-full" style={{ background: s.accent }}>
                  <Check size={10} className="text-white" />
                </span>
                {t}
              </li>
            ))}
          </ul>

          {/* Actions */}
          <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
              {STEPS.map((_, idx) => (
                <span
                  key={idx}
                  className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-8" : "w-2"}`}
                  style={{ background: idx <= i ? s.accent2 : "rgba(255,255,255,0.12)", boxShadow: idx === i ? `0 0 8px ${s.accent2}` : "none" }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                data-testid="onboarding-skip"
                onClick={dismiss}
                className="text-sm font-semibold text-slate-400 hover:text-white px-4 py-2 rounded-xl hover:bg-white/5"
              >
                Skip
              </button>
              {s.to && (
                <button
                  data-testid={`onboarding-step-${i + 1}-cta`}
                  onClick={goTo}
                  className="btn-glass inline-flex items-center gap-2 text-sm"
                >
                  {s.cta}
                </button>
              )}
              <button
                data-testid="onboarding-next"
                onClick={next}
                className="btn-neon inline-flex items-center gap-2 text-sm"
              >
                {i < STEPS.length - 1 ? "Next" : "Get started"}
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Small "Restart tour" button for the Settings page
export function RestartOnboardingButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid="restart-onboarding"
        onClick={() => setOpen(true)}
        className="btn-glass inline-flex items-center gap-2 text-sm"
      >
        <Sparkles size={14} /> Replay welcome tour
      </button>
      {open && <Onboarding forceOpen onClose={() => setOpen(false)} />}
    </>
  );
}
